<template>
  <div class="chart-wrapper">
    <Header @toggle-full-screen="toggleFullScreen">
      <div v-if="isComparison" :class="{ 'text-primary': isPrimary }">
        {{ $t("message.reviews.categories.comparedPeriod") }}:
        <b>{{ date.date_from }} - {{ date.date_to }}</b>
      </div>
    </Header>
    <div v-if="showChart" :id="id" class="chart" :style="chartSize"></div>
    <StateContainer
      v-if="showStateContainer"
      :is-loading="isLoading"
      :show-no-data="showNoData"
    />
    <Dropdown
      v-if="showDropdown"
      ref="dropdown"
      @show-details="goToDetails"
      @show-reviews-list="goToReviewsList"
    />
    <Legend :title="$t('message.sentiment.heatmap.legendTitle')" />
  </div>
</template>

<script>
import { FORMAT } from "@/config/date-format";
import moment from "moment";
import * as echarts from "echarts/core";
import { TreemapChart } from "echarts/charts";
import { TooltipComponent } from "echarts/components";
import { CanvasRenderer } from "echarts/renderers";
import Legend from "@/components/Reviews/Categories/ReviewsCategoriesLegend.vue";
import Header from "@/components/Reviews/Categories/ReviewsCategoriesHeatmapHeader.vue";
import StateContainer from "@/components/Reviews/Categories/ReviewsCategoriesHeatmapStateContainer.vue";
import { getChartOptions } from "@/config/categoryHeatmapChartConfig";
import Dropdown from "@/components/Reviews/Categories/ReviewsCategoriesHeatmapDropdown.vue";
import { GTM_EVENT_NAME } from "@/enums/gtm-events";
import constants from "@/config/constants";

const DEFAULT_CHART_SIZE = { width: "100%", height: "276px" };
const FULLSCREEN_CHART_SIZE = { width: "1000px", height: "1500px" };

export default {
  name: "ReviewsCategoriesHeatmapChart",
  components: {
    Legend,
    Header,
    StateContainer,
    Dropdown
  },
  props: {
    id: {
      type: String,
      required: true
    },
    isLoading: {
      type: Boolean,
      default: false
    },
    isComparison: {
      type: Boolean,
      default: false
    },
    isPrimary: {
      type: Boolean,
      default: false
    },
    data: {
      type: Array,
      required: true
    },
    date: {
      type: [Object, null]
    },
    color: {
      type: Array,
      required: true
    }
  },
  data() {
    return {
      isFullScreen: false,
      chart: null,
      chartSize: DEFAULT_CHART_SIZE,
      showDropdown: false,
      selectedCategoryId: null
    };
  },
  computed: {
    showStateContainer() {
      return this.showNoData || this.isLoading;
    },
    showNoData() {
      return !this.isLoading && this.data.length == 0;
    },
    showChart() {
      return !this.isLoading && this.data.length > 0;
    }
  },
  watch: {
    showChart: {
      async handler(current) {
        if (current && this.data.length) {
          await this.$nextTick();
          this.createChart();
          return;
        }
        const element = document.getElementById(this.id);
        if (element && this.chart) {
          this.chart.dispose();
          this.chart = null;
          document.getElementById(this.id).innerHTML = "";
        }
      }
    }
  },
  created() {
    echarts.use([TreemapChart, TooltipComponent, CanvasRenderer]);
    window.addEventListener("click", this.clickOutsideChartHandler);
  },
  beforeDestroy() {
    window.removeEventListener("click", this.clickOutsideChartHandler);
  },
  methods: {
    createChart() {
      this.chart = echarts.init(document.getElementById(this.id));
      this.chart.setOption(getChartOptions(this.data, this.color));
      this.chart.on("click", this.onChartClickHandler);
    },

    async onChartClickHandler(e) {
      e.event.event.stopPropagation();
      this.showDropdown = true;
      this.selectedCategoryId = e.data.id;
      await this.$nextTick();
      this.setDropdownPlacement(e.event.offsetX, e.event.offsetY);
    },

    setDropdownPlacement(offsetX, offsetY) {
      const {
        dropdown: { $el: element }
      } = this.$refs;
      const dropdownOffsetWidth = element.offsetWidth;
      const isOverflowingX =
        offsetX + dropdownOffsetWidth > this.chart.getWidth();

      Object.assign(element.style, {
        top: `${offsetY}px`,
        left: `${isOverflowingX ? offsetX - dropdownOffsetWidth : offsetX}px`
      });
    },

    clickOutsideChartHandler() {
      this.showDropdown = false;
    },

    toggleFullScreen() {
      this.isFullScreen = !this.isFullScreen;
      this.chartSize = this.isFullScreen
        ? FULLSCREEN_CHART_SIZE
        : DEFAULT_CHART_SIZE;
      this.$nextTick(this.chart.resize);

      this.$emit("toggle-fullscreen");
    },

    goToDetails() {
      const queries = { ...this.$route.query };
      this.$gtm.trackCustomEvent({
        event: GTM_EVENT_NAME.REVIEW_CATEGORY_ANALYTICS_OVERVIEW_HEATMAP_ACTION,
        action: "show_details"
      });
      this.$router.push({
        name: "reviews-category-details",
        query: {
          ...queries,
          tag_id: this.selectedCategoryId
        }
      });
    },
    goToReviewsList() {
      const { selectedFrom, selectedTo, ...queries } = this.$route.query;
      let date_from = selectedFrom;
      let date_to = selectedTo;
      if (this.$route.query.dateType === constants.fullPeriod) {
        date_from = moment(constants.absoluteStartDate).format(FORMAT.DATE);
        date_to = moment(Date.now()).format(FORMAT.DATE);
      }
      this.$gtm.trackCustomEvent({
        event: GTM_EVENT_NAME.REVIEW_CATEGORY_ANALYTICS_OVERVIEW_HEATMAP_ACTION,
        action: "show_reviews"
      });
      this.$router.push({
        name: "reviews-list",
        query: {
          ...queries,
          date_from,
          date_to,
          tag_id: `${this.selectedCategoryId}`
        }
      });
    }
  }
};
</script>

<style lang="scss" scoped>
::v-deep .chart-tooltip {
  border-radius: 20px !important;
}

.chart-wrapper {
  display: flex;
  flex-direction: column;
  margin: $spacing * 3 auto 0 auto;
  width: 100%;
  position: relative;
}

.chart {
  margin: 0 auto $spacing * 2.5 auto;
  overflow: hidden;
  border-radius: 25px;
}
</style>
